import { isLngLat } from "../../shared/geo.ts";
import type maplibregl from "maplibre-gl";

/** Where list-card “away” distances are measured from: geolocation, else map center. */
export type NearbyViewerAt = { lat: number; lng: number };

/**
 * Watches geolocation when permitted and falls back to the home map center.
 * Never uses a hard-coded default coordinate pair for distance.
 */
export function createNearbyViewer(getMap: () => maplibregl.Map): {
  start: (onUpdate: () => void) => void;
  stop: () => void;
  at: () => NearbyViewerAt | null;
} {
  let geo: NearbyViewerAt | null = null;
  let watchId: number | null = null;

  const applyCoords = (lat: number, lng: number, onUpdate: () => void): void => {
    if (!isLngLat([lng, lat])) return;
    const next = { lat, lng };
    if (geo && geo.lat === next.lat && geo.lng === next.lng) return;
    geo = next;
    onUpdate();
  };

  return {
    start(onUpdate: () => void): void {
      if (typeof navigator === "undefined" || !navigator.geolocation) return;
      navigator.geolocation.getCurrentPosition(
        (pos) => applyCoords(pos.coords.latitude, pos.coords.longitude, onUpdate),
        () => undefined,
        { enableHighAccuracy: false, maximumAge: 120_000, timeout: 15_000 },
      );
      watchId = navigator.geolocation.watchPosition(
        (pos) => applyCoords(pos.coords.latitude, pos.coords.longitude, onUpdate),
        () => undefined,
        { enableHighAccuracy: false, maximumAge: 60_000, timeout: 20_000 },
      );
    },
    stop(): void {
      if (watchId != null && typeof navigator !== "undefined" && navigator.geolocation) {
        navigator.geolocation.clearWatch(watchId);
      }
      watchId = null;
    },
    at(): NearbyViewerAt | null {
      if (geo && isLngLat([geo.lng, geo.lat])) return geo;
      try {
        const c = getMap().getCenter();
        if (isLngLat([c.lng, c.lat])) return { lat: c.lat, lng: c.lng };
      } catch {
        /* map torn down */
      }
      return null;
    },
  };
}
